import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { getSmsDashboardConversationsKey } from './useConversations';

import type {
  IConversation,
  IConversationsListPages,
} from 'infrastructure/interfaces';
import type { RootState } from 'store';

interface ISocketData {
  conversationId: number;
  read: boolean;
  rpmTotalUnreadCount: number;
  ccmTotalUnreadCount: number;
  unregisteredRpmTotalUnreadCount: number;
  unregisteredCcmTotalUnreadCount: number;
}

export const useUpdateConversationState = () => {
  const queryClient = useQueryClient();
  const { conversationType, filters } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  const updateConversationState = (
    socketData: ISocketData,
    needCloseConversation = false,
  ) => {
    const conversationKey = getSmsDashboardConversationsKey({
      type: conversationType,
      read: filters.read,
      closed: filters.closed,
      lastMessageDate: filters.lastMessageDate,
      searchQuery: filters.searchQuery,
    });

    const conversations =
      queryClient.getQueryData<IConversationsListPages>(conversationKey);

    if (!conversations) return;

    let wasUpdated = false;

    const updatedPages = conversations.pages.map((page) => {
      const updatedItems = page.items.reduce<IConversation[]>((acc, item) => {
        if (item.id !== socketData.conversationId) {
          acc.push(item);
          return acc;
        }

        wasUpdated = true;

        if (!needCloseConversation) {
          acc.push({
            ...item,
            read: socketData.read,
            unreadMessageCount: 0,
            closed: false,
          });
        }

        return acc;
      }, []);

      return { ...page, items: updatedItems };
    });

    if (wasUpdated) {
      queryClient.setQueryData<IConversationsListPages>(
        conversationKey,
        (old) => (old ? { ...old, pages: updatedPages } : old),
      );
    }
  };

  return { updateConversationState };
};
